/**
 * Laying the owner's edits over the copy the marketing site ships with.
 *
 * A content module's exports mix plain data (strings, numbers, lists) with
 * things JSON cannot carry - lucide icons, components. The owner edits only
 * the plain data; the rest always comes from the shipped defaults, so an edit
 * can never remove an icon or put something unrenderable where a component
 * belongs. Shapes are the shipped ones too: a field the defaults do not have,
 * or a value of the wrong type, is ignored rather than handed to a component
 * that was never written to expect it.
 */

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };

/**
 * Fields the code looks things up by - a feature's slug, a row's key, which
 * icon to draw. Shown to the owner but never taken from an edit: changing one
 * would break a link or a lookup rather than change any wording.
 */
export const READONLY_KEYS: ReadonlySet<string> = new Set(['slug', 'key', 'id', 'icon']);

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value) as unknown;
  // React elements and forwardRef/memo components (lucide icons) are objects
  // too; they carry `$$typeof` and are never data.
  return (proto === Object.prototype || proto === null) && !('$$typeof' in value);
}

function isEditableLeaf(value: unknown): value is string | number | boolean | null {
  return (
    value === null ||
    typeof value === 'string' ||
    (typeof value === 'number' && Number.isFinite(value)) ||
    typeof value === 'boolean'
  );
}

/** A copy that clones plain data and keeps everything else by reference. */
export function cloneContent<T>(value: T): T {
  if (Array.isArray(value)) return value.map((item: unknown) => cloneContent(item)) as T;
  if (isPlainObject(value)) {
    const copy: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value)) copy[key] = cloneContent(item);
    return copy as T;
  }
  return value;
}

/**
 * The JSON part of a value: what the editor shows and what gets saved.
 * Returns undefined for something with no editable data at all (an icon).
 */
export function toEditable(value: unknown): JsonValue | undefined {
  if (isEditableLeaf(value)) return value;
  if (Array.isArray(value)) {
    const items = value.map(toEditable);
    // A list that holds icons or components is the code's, not the owner's.
    return items.some((item) => item === undefined) ? undefined : (items as JsonValue[]);
  }
  if (isPlainObject(value)) {
    const out: JsonObject = {};
    for (const [key, item] of Object.entries(value)) {
      const editable = toEditable(item);
      if (editable !== undefined) out[key] = editable;
    }
    return out;
  }
  return undefined;
}

/**
 * The fields every item of a list may have, with an example value for each -
 * the union across the shipped items, so an optional field (a price only some
 * plans show) can still be added to an item that started without it.
 */
export function itemShape(items: readonly unknown[]): Record<string, unknown> | null {
  const objects = items.filter(isPlainObject);
  if (objects.length === 0) return null;
  const shape: Record<string, unknown> = {};
  for (const item of objects) {
    for (const [key, value] of Object.entries(item)) {
      if (shape[key] === undefined || shape[key] === null) shape[key] = value;
    }
  }
  return shape;
}

function sameKind(base: unknown, override: unknown): boolean {
  if (base === null || base === undefined) return isEditableLeaf(override);
  if (Array.isArray(base)) return Array.isArray(override);
  if (isPlainObject(base)) return isPlainObject(override);
  if (isEditableLeaf(base)) return typeof base === typeof override || override === null;
  return false;
}

function mergeObject(
  base: Record<string, unknown>,
  override: Record<string, unknown>,
  shape: Record<string, unknown> = base,
): Record<string, unknown> {
  const out: Record<string, unknown> = cloneContent(base);
  for (const [key, value] of Object.entries(override)) {
    if (!(key in shape) || READONLY_KEYS.has(key)) continue;
    const reference = key in base ? base[key] : shape[key];
    // An optional field the owner added: copy a sibling's kind, never its icon.
    if (
      !(key in base) &&
      !isEditableLeaf(reference) &&
      !Array.isArray(reference) &&
      !isPlainObject(reference)
    ) {
      continue;
    }
    out[key] = key in base ? mergeValue(base[key], value) : mergeValue(blankLike(reference), value);
  }
  return out;
}

/** A value of the same kind as ``example`` with no content, for new items. */
function blankLike(example: unknown): unknown {
  if (typeof example === 'string') return '';
  if (typeof example === 'number') return 0;
  if (typeof example === 'boolean') return false;
  if (Array.isArray(example)) return [];
  if (isPlainObject(example)) return cloneContent(example);
  return example;
}

function mergeArray(base: readonly unknown[], override: readonly unknown[]): unknown[] {
  const shape = itemShape(base);
  return override
    .map((value, index) => {
      // Items are matched by position: reordering moves the text, while the
      // icon (code, not content) stays with the slot - or, past the shipped
      // list, comes from the last shipped item.
      const slot = index < base.length ? base[index] : base[base.length - 1];
      if (slot === undefined) return isEditableLeaf(value) ? value : undefined;
      if (isPlainObject(slot) && isPlainObject(value))
        return mergeObject(slot, value, shape ?? slot);
      return mergeValue(slot, value);
    })
    .filter((item) => item !== undefined);
}

/** ``base`` with ``override`` laid over it, without touching either. */
export function mergeValue(base: unknown, override: unknown): unknown {
  if (override === undefined || !sameKind(base, override)) return cloneContent(base);
  if (Array.isArray(base)) return mergeArray(base, override as unknown[]);
  if (isPlainObject(base)) return mergeObject(base, override as Record<string, unknown>);
  return override;
}

/**
 * Make ``target`` hold ``source``'s contents while keeping its identity, so
 * every module that imported the exported object sees the new copy.
 */
export function replaceInPlace(target: unknown, source: unknown): void {
  if (Array.isArray(target) && Array.isArray(source)) {
    target.splice(0, target.length, ...(source as unknown[]));
    return;
  }
  if (isPlainObject(target) && isPlainObject(source)) {
    for (const key of Object.keys(target)) {
      if (!(key in source)) delete target[key];
    }
    Object.assign(target, source);
  }
}
